<script setup>
import AssetFilters from '../../components/forms/filters/events/Asset';
import urlPaginationRestore from '@/mixins/urlPaginationRestore';
import AssetsTable from '../../components/table/events/Assets';
</script>

<template>
  <div data-view="assets">
    <!-- route to popup [[src/components/dialog/events/Asset.vue]] -->
    <!-- All popups will appear here -->
    <router-view v-slot="slotProps">
      <component :is="slotProps?.Component" @saved="onSaved" />
    </router-view>

    <layout-logged-frame :title="$t('event_assets')">
      <template v-slot:header-actions>
        <header-actions :actions="headerActions" />
      </template>

      <template v-slot:content>
        <AssetFilters
          @filter="onFilter"
          @clearFilters="onClearFilters"
        >
          <template v-slot:phone-actions>
            <sort-by-select
              :columns="assetsColumns"
              :pagination="pagination"
              :on-update-pagination="onUpdatePagination"
            />
          </template>
        </AssetFilters>

        <AssetsTable
          :on-add="addAction?.onClick"
          :add-label="addAction?.title"
          :items="assets"
          :pagination="pagination"
          :is-loading="isLoading"
          :pages="totalPages"
          :on-select-item="selectItem"
          :on-update-pagination="onUpdatePagination"
          :sync-to-url="true"
          @init-pagination="onInitPagination"
          :on-change-page="onChangePage"
        />
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
import assetsColumns from '../../config/events/assetsColumns';
import { isStr } from '@/utils';

export default {
  mixins: [urlPaginationRestore],
  data() {
    return {
      assetsColumns,
      pagination: {
        order: 'desc' ,
        page: 1,
        rowsPerPage: 15,
        perPage: 15,
        sortBy: 'created_at',
        totalItems: 0,
        search: '',
      },
      totalPages: 0,
      query: {
        per_page: 15,
      },
      isLoading: false,
    };
  },
  computed: {
    addAction() {
      return this.headerActions.find(({ key, isDisabled }) => key === 'add' && !isDisabled);
    },
    headerActions() {
      return [
        {
          key: 'add',
          title: this.$t('new_event_assets'),
          onClick: this.openNewEventAssetsModal,
        },
      ];
    },
    assets() {
      return this.$store.getters['events/assets'];
    },
  },
  methods: {
    // Emitted by the dialog routes after a successful save. Keeps the
    // refetch in this view, with this view's own query, and out of the URL.
    onSaved() {
      this.isLoading = true;
      this.getAssets();
    },
    openNewEventAssetsModal() {
      this.$router.push({ name: 'event-assets-new', query: this.$route.query });
    },
    getAssets() {
      this.isLoading = true;
      this.pagination.order = this.query.order;

      return this.$store
        .dispatch('events/getAssets', this.query)
        .then((res) => {
          const { total, current_page, last_page } = res.assetsMeta;

          this.pagination.page = current_page;
          this.pagination.lastPage = last_page;
          this.pagination.totalItems = total;
          this.totalPages = last_page;
          this.isLoading = false;

          return res;
        })
        .catch((err) => {
          this.isLoading = false;
          return err;
        });
    },
    selectItem(asset) {
      this.$store.dispatch('events/setActiveAsset', asset).then(() => {
        this.$router.push({name: 'event-assets-details', params: {id: asset.id}, query: this.$route.query});
      });
    },
    onUpdatePagination({ page, order, sortBy }) {
      const { query, pagination } = this;
      this.pagination.order = order;
      this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

      const nextQuery = {
        ...query,
        page,
        order,
        sort: isStr(sortBy) ? sortBy : pagination.sortBy,
      };

      this.query = nextQuery;
      this.runInitialFetch(() => this.getAssets());
    },
    onChangePage(page) {
      const { query, pagination } = this;
      const { order, sortBy } = pagination;
      const nextQuery = {
        ...query,
        page,
        order,
        sort: sortBy,
      };

      this.query = nextQuery;
      this.getAssets();
    },
    onFilter(filters) {
      this.query = {
        ...this.query,
        ...filters,
        page: 1
      };

      this.runInitialFetch(() => this.getAssets());
    },
    onClearFilters() {
      const { sortBy, order } = this.pagination;
      this.query = { sort: sortBy, order, per_page: 15, page: 1 };
      this.getAssets();
    },
  },
};
</script>

<style lang="scss">
@use '../../assets/styles/theme' as *;

[data-view='assets'] {
  .trigger.download-pdf {
    display: flex;
    justify-content: flex-start;
    align-items: center;

    .v-icon {
      margin: 0 6px 0 0;
    }
  }

  .v-datatable {
    min-width: $table-min-width;
  }
}
</style>
